import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQ_DATA = [
  {
    category: 'Product & Materials',
    questions: [
      {
        q: 'What is BFRP rebar?',
        a: 'BFRP (Basalt Fiber Reinforced Polymer) rebar is a composite reinforcement made from volcanic basalt rock fibers embedded in an epoxy resin matrix. It offers 2× the tensile strength of Grade 60 steel while being completely immune to corrosion.'
      },
      {
        q: 'What is the difference between BFRP and GFRP?',
        a: 'BFRP uses basalt fibers (from volcanic rock) while GFRP uses glass fibers. BFRP offers higher tensile strength, better temperature resistance (-76°F to 572°F), and superior alkaline resistance. GFRP is more cost-effective and has a longer code history. Both are corrosion-immune.'
      },
      {
        q: 'What sizes are available?',
        a: 'GEObar B and GEObar FG are available in #2 (6mm), #3 (10mm), #4 (12mm), #5 (16mm), #6 (20mm), and #8 (25mm). Custom sizes may be available for large orders. Contact us for specific requirements.'
      },
      {
        q: 'Can FRP rebar be bent?',
        a: 'FRP rebar cannot be field-bent like steel. Bends must be manufactured into the bar during production. We offer standard bend configurations (90°, 180° hooks) and can produce custom bent shapes for your project.'
      },
    ]
  },
  {
    category: 'Engineering & Design',
    questions: [
      {
        q: 'How do I design with FRP rebar?',
        a: 'FRP design follows ACI 440.1R-15 guidelines. Key difference from steel: FRP designs are typically deflection-governed rather than strength-governed due to lower modulus of elasticity. We recommend consulting the ACI 440 guide or contacting our technical team for design assistance.'
      },
      {
        q: 'What is the modulus of elasticity?',
        a: 'GEObar B: 6.5-10 Msi (45-70 GPa). GEObar FG: 5.8-8.7 Msi (40-60 GPa). Steel: 29 Msi. The lower modulus means FRP members will deflect more under load, requiring deflection checks in design.'
      },
      {
        q: 'Can FRP replace steel in any application?',
        a: 'FRP excels in corrosive environments (marine, coastal, parking structures, wastewater) and electromagnetic-sensitive applications (MRI facilities). For standard applications without corrosion concerns, steel may still be more economical. We can help evaluate if FRP is right for your project.'
      },
      {
        q: 'What codes and standards apply?',
        a: 'Primary standards include ACI 440.1R-15 (design guide), ACI 440.11-22 (building code for GFRP), ASTM D7205 (tensile test method), and ASTM D7957 (GFRP specification). All GEO products are manufactured to meet these standards.'
      },
    ]
  },
  {
    category: 'Installation & Handling',
    questions: [
      {
        q: 'How do I cut FRP rebar?',
        a: 'Use diamond blades, abrasive cut-off wheels, or carbide-tipped saws. Standard steel-cutting tools are not recommended. Always wear safety glasses and a dust mask when cutting. FRP produces fine dust, not sharp metal fragments.'
      },
      {
        q: 'What tie wire should I use?',
        a: 'Use coated wire ties, nylon zip ties, or plastic rebar clips. Avoid bare metal tie wire in direct contact with FRP to prevent surface damage and galvanic concerns. Standard plastic "chairs" work for positioning.'
      },
      {
        q: 'How should FRP be stored?',
        a: 'Store on flat surfaces, supported every 4-6 feet to prevent sagging. Keep away from prolonged direct UV exposure until installation. FRP can be stored outdoors but covered storage is preferred for extended periods.'
      },
      {
        q: 'Is FRP lighter than steel?',
        a: 'Yes, FRP is approximately 75% lighter than steel. A #4 FRP bar weighs ~0.13 lb/ft vs 0.668 lb/ft for steel. This significantly reduces handling effort, transportation costs, and crane requirements.'
      },
    ]
  },
  {
    category: 'Cost & Business',
    questions: [
      {
        q: 'Is FRP more expensive than steel?',
        a: 'Initial material cost is typically higher than steel (varies by market conditions). However, lifecycle cost is often lower due to: zero corrosion maintenance, longer service life (100+ vs 25 years), reduced labor (lighter weight), and eliminated concrete repair costs.'
      },
      {
        q: 'What is the lead time?',
        a: 'Standard products typically ship within 2-4 weeks. Large orders or custom configurations may require 4-8 weeks. Contact us early in your project planning for accurate lead times.'
      },
      {
        q: 'Do you offer samples?',
        a: 'Yes! We provide free sample kits including cut sections of GEObar B, GEObar FG, and GEOfabric. Request samples through our website to evaluate the materials before specifying.'
      },
      {
        q: 'What are your payment terms?',
        a: 'We offer Net-30 terms for established commercial accounts. New accounts may require prepayment or credit approval. Contact our sales team to establish an account.'
      },
    ]
  },
  {
    category: 'Performance & Durability',
    questions: [
      {
        q: 'Does FRP corrode?',
        a: 'No. FRP is completely immune to corrosion, rust, and chloride attack. This is the primary advantage over steel in marine, coastal, and chemically exposed environments.'
      },
      {
        q: 'What is the expected service life?',
        a: 'FRP-reinforced structures are designed for 100+ year service life in aggressive environments. Compare this to 15-25 years for steel rebar in marine/chloride conditions before significant corrosion issues.'
      },
      {
        q: 'How does FRP perform in fire?',
        a: 'FRP has lower fire resistance than steel. The resin matrix begins to soften around 250-300°F. For fire-critical applications, additional concrete cover or fire protection may be required. Consult ACI 440 for fire design guidance.'
      },
      {
        q: 'Is FRP affected by UV exposure?',
        a: 'Once embedded in concrete, UV is not a concern. For exposed applications, UV-resistant coatings are available. Standard storage should minimize prolonged direct sunlight exposure.'
      },
    ]
  },
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-border-dark">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-start justify-between text-left hover:bg-surface-dark/30 transition-colors"
        data-testid="faq-toggle"
      >
        <span className="text-stone font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp size={20} className="text-gold shrink-0" />
        ) : (
          <ChevronDown size={20} className="text-stone/40 shrink-0" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 text-stone/60 leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState(FAQ_DATA[0].category);

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="faq-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-4">
              <HelpCircle size={24} className="text-gold" />
              <span className="tech-label">//COMMON_QUESTIONS</span>
            </div>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              Frequently Asked <span className="text-gold">Questions</span>
            </h1>
            <p className="text-stone/50 max-w-2xl text-lg">
              Answers to common questions about composite reinforcement materials, 
              design considerations, and working with Standard Composites.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Category Navigation */}
            <div className="lg:col-span-1">
              <nav className="sticky top-28 space-y-2">
                {FAQ_DATA.map(category => (
                  <button
                    key={category.category}
                    onClick={() => setActiveCategory(category.category)}
                    className={`w-full text-left p-4 text-sm transition-all ${
                      activeCategory === category.category
                        ? 'bg-gold/10 border-l-2 border-gold text-gold'
                        : 'text-stone/50 hover:text-stone border-l-2 border-transparent'
                    }`}
                  >
                    {category.category}
                  </button>
                ))}
              </nav>
            </div>

            {/* Questions */}
            <div className="lg:col-span-3">
              {FAQ_DATA.map(category => (
                <div 
                  key={category.category}
                  className={activeCategory === category.category ? 'block' : 'hidden'}
                >
                  <h2 className="heading-section text-gold text-xl mb-6">{category.category}</h2>
                  <div className="space-y-4">
                    {category.questions.map((item, index) => (
                      <FAQItem key={index} question={item.q} answer={item.a} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-16 lg:py-24 border-t border-border-dark bg-surface-dark/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="heading-display text-stone text-3xl mb-4">Still Have Questions?</h2>
          <p className="text-stone/50 mb-8">
            Our technical team is ready to help with project-specific questions.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
